import {
  POINT_CLOUD_BAND_POINT_LIMIT,
  POINT_CLOUD_CARD_POINT_LIMIT,
  type PointCloudPointCountBand,
  type PointCloudPresentation,
  type PointCloudRendererId,
  type PointCloudTimingHook,
  type PointCloudViewerTiming,
  type PointCloudViewerTimingVital,
} from './types';

/**
 * Names of metrics the workspace card already emits. New `pointCloud.*`
 * timings do not replace these.
 */
export const EXISTING_POINT_CLOUD_METRICS = {
  tileTotal: 'mediaBrowserPanelTile.total',
  tileLegacy: 'mediaBrowserPanelTileTracking',
  productEvent: '3D Point Cloud Viewed',
} as const;

export const EXISTING_POINT_CLOUD_PRODUCT_EVENT_TYPES = [
  'preview',
  'fullscreen',
] as const;

export const POINT_CLOUD_TIMING_VITALS = {
  parse: 'pointCloud.parse',
  snapshotRender: 'pointCloud.snapshotRender',
  fullscreenReady: 'pointCloud.fullscreenReady',
} as const;

export const POINT_CLOUD_VIEWER_TIMING_VITALS = [
  POINT_CLOUD_TIMING_VITALS.snapshotRender,
  POINT_CLOUD_TIMING_VITALS.fullscreenReady,
] as const;

export type PointCloudTimingSample = {
  renderer: PointCloudRendererId;
  durationMs: number;
  sourcePointCount: number;
};

export function pointCountBand(pointCount: number): PointCloudPointCountBand {
  if (pointCount <= POINT_CLOUD_CARD_POINT_LIMIT) {
    return 'le300k';
  }
  if (pointCount <= POINT_CLOUD_BAND_POINT_LIMIT) {
    return '300k_1M';
  }
  return 'gt1M';
}

function viewerTiming(
  vital: PointCloudViewerTimingVital,
  presentation: PointCloudPresentation,
  sample: PointCloudTimingSample
): PointCloudViewerTiming {
  return {
    vital,
    presentation,
    renderer: sample.renderer,
    durationMs: sample.durationMs,
    pointCountBand: pointCountBand(sample.sourcePointCount),
  };
}

/** Report card screenshot duration after the renderer returns. */
export function reportCardSnapshotTiming(
  onTiming: PointCloudTimingHook | undefined,
  sample: PointCloudTimingSample
): void {
  onTiming?.(
    viewerTiming(POINT_CLOUD_TIMING_VITALS.snapshotRender, 'card', sample)
  );
}

/** Report fullscreen interactive duration after the renderer mounts. */
export function reportFullscreenReadyTiming(
  onTiming: PointCloudTimingHook | undefined,
  sample: PointCloudTimingSample
): void {
  onTiming?.(
    viewerTiming(
      POINT_CLOUD_TIMING_VITALS.fullscreenReady,
      'fullscreen',
      sample
    )
  );
}
